import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Network, Server, TrendingUp, TrendingDown, DollarSign, 
  Activity, BarChart3, Gauge, Zap, Radio, Globe, Clock,
  AlertTriangle, CheckCircle, PieChart
} from 'lucide-react';

import { authFetch, API_URL } from '../utils/authFetch';

const NetFlowAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [opexData, setOpexData] = useState(null);
  const [capacityData, setCapacityData] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [vranStatus, setVranStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [dashboard, opex, capacity, traffic, vran] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/netflow/dashboard/overview`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/netflow/opex-analysis`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/netflow/capacity-planning`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/netflow/traffic-analysis`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/netflow/virtual-ran/status`).then(r => r.json())
      ]);
      setDashboardData(dashboard);
      setOpexData(opex);
      setCapacityData(capacity);
      setTrafficData(traffic);
      setVranStatus(vran);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">NetFlow Analytics</h2>
          <p className="text-neutral-400">Asset Utilization & OpEx Optimization for Telecom Operators</p>
        </div>
        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
          <Radio className="w-3 h-3 mr-1" /> Virtual RAN Connected
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="assets">Assets</TabsTrigger>
          <TabsTrigger value="opex">OpEx Analysis</TabsTrigger>
          <TabsTrigger value="capacity">Capacity Planning</TabsTrigger>
          <TabsTrigger value="traffic">Traffic Analysis</TabsTrigger>
          <TabsTrigger value="vran">Virtual RAN</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {dashboardData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Server className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-neutral-400">Total Assets</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{dashboardData.summary.total_assets}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      <span className="text-xs text-neutral-400">Bandwidth</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{dashboardData.summary.total_bandwidth_tbps} Tbps</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Gauge className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-neutral-400">Utilization</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{dashboardData.summary.current_utilization_avg}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-neutral-400">Flows/24h</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{(dashboardData.summary.total_flows_24h / 1000000).toFixed(1)}M</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      <span className="text-xs text-neutral-400">Anomalies</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{dashboardData.summary.anomalies_detected}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-neutral-400">Active</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{dashboardData.summary.active_assets}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Utilization by Type */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Utilization by Asset Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(dashboardData.utilization_by_type).map(([type, data]) => (
                      <div key={type} className="bg-[#1a1a1a] rounded-lg p-4">
                        <p className="text-sm text-neutral-400 capitalize">{type.replace('_', ' ')}</p>
                        <p className="text-xl font-bold text-white">{data.avg_util}%</p>
                        <p className="text-xs text-neutral-500">{data.count} assets</p>
                        <div className="mt-2 h-2 bg-black/30 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${data.avg_util > 80 ? 'bg-red-500' : data.avg_util > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${data.avg_util}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Traffic Distribution */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Traffic Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(dashboardData.traffic_distribution).map(([app, percent]) => (
                        <div key={app} className="flex items-center justify-between">
                          <span className="text-sm text-neutral-300">{app.replace('_', ' ')}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-2 bg-black/30 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <span className="text-sm text-white w-12 text-right">{percent}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Top Consumers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dashboardData.top_consumers.slice(0, 6).map((consumer, i) => (
                        <div key={`consumer-${i}`} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <div>
                            <p className="text-sm text-white font-mono">{consumer.ip}</p>
                            <p className="text-xs text-neutral-500 capitalize">{consumer.type}</p>
                          </div>
                          <Badge variant="outline" className="text-orange-400 border-orange-500/30">
                            {consumer.bandwidth_gbps} Gbps
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="opex" className="space-y-6">
          {opexData && (
            <>
              {/* OpEx Summary */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-orange-500/20 to-amber-500/10 border-orange-500/30">
                  <CardContent className="p-4">
                    <DollarSign className="w-8 h-8 text-orange-400 mb-2" />
                    <p className="text-xs text-neutral-300">Monthly OpEx</p>
                    <p className="text-2xl font-bold text-white">${(opexData.summary.total_monthly_opex / 1000).toFixed(0)}K</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <TrendingDown className="w-8 h-8 text-green-400 mb-2" />
                    <p className="text-xs text-neutral-400">Savings Potential</p>
                    <p className="text-2xl font-bold text-white">${(opexData.total_savings_potential.monthly / 1000).toFixed(0)}K</p>
                    <p className="text-xs text-green-400">{opexData.total_savings_potential.percent_of_opex}% reduction</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <BarChart3 className="w-8 h-8 text-blue-400 mb-2" />
                    <p className="text-xs text-neutral-400">OpEx/Gbps</p>
                    <p className="text-2xl font-bold text-white">${opexData.summary.opex_per_gbps.toFixed(0)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Gauge className="w-8 h-8 text-purple-400 mb-2" />
                    <p className="text-xs text-neutral-400">vs Industry Benchmark</p>
                    <p className="text-2xl font-bold text-white">{opexData.summary.industry_benchmark_ratio}x</p>
                  </CardContent>
                </Card>
              </div>

              {/* Cost Breakdown */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">OpEx Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      {Object.entries(opexData.breakdown).map(([category, data]) => (
                        <div key={category} className="flex items-center justify-between">
                          <span className="text-sm text-neutral-300 capitalize">{category.replace('_', ' ')}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-black/30 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                                style={{ width: `${data.percent}%` }}
                              />
                            </div>
                            <span className="text-sm text-white w-16 text-right">${(data.amount / 1000).toFixed(0)}K</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-4">Peer Comparison</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">Your OpEx Ratio</span>
                          <span className="text-white">{opexData.peer_comparison.your_opex_ratio}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">Industry Average</span>
                          <span className="text-white">{opexData.peer_comparison.industry_avg}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">Best in Class</span>
                          <span className="text-green-400">{opexData.peer_comparison.best_in_class}%</span>
                        </div>
                        <div className="flex justify-between text-sm border-t border-white/10 pt-2 mt-2">
                          <span className="text-neutral-400">Improvement Gap</span>
                          <span className="text-orange-400">{opexData.peer_comparison.improvement_gap}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Optimization Opportunities */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Optimization Opportunities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {opexData.optimization_opportunities.map((opp, i) => (
                      <div key={opp.id || opp.name || i} className="bg-[#1a1a1a] rounded-lg p-4 flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-white font-semibold">{opp.category}</h4>
                            <Badge variant="outline" className={
                              opp.implementation_effort === 'low' ? 'text-green-400 border-green-500/30' :
                              opp.implementation_effort === 'medium' ? 'text-yellow-400 border-yellow-500/30' :
                              'text-red-400 border-red-500/30'
                            }>
                              {opp.implementation_effort}
                            </Badge>
                          </div>
                          <p className="text-sm text-neutral-400">{opp.description}</p>
                          <p className="text-xs text-neutral-500 mt-1">Payback: {opp.payback_months} months</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-400">${(opp.potential_savings / 1000).toFixed(0)}K</p>
                          <p className="text-xs text-neutral-500">monthly savings</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="vran" className="space-y-6">
          {vranStatus && (
            <>
              <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">Virtual RAN Integration</h3>
                      <p className="text-neutral-300">Connect with operator data flows without physical infrastructure</p>
                    </div>
                    <Badge className="bg-green-500 text-white">
                      {vranStatus.vran_status.toUpperCase()}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Connected Operators */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Connected Operators</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {vranStatus.connections.map((conn, i) => (
                      <div key={conn.id || conn.name || i} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                              <Globe className="w-5 h-5 text-black" />
                            </div>
                            <div>
                              <h4 className="text-white font-semibold">{conn.operator}</h4>
                              <p className="text-xs text-neutral-500">{conn.connection_type}</p>
                            </div>
                          </div>
                          <Badge className={conn.status === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {conn.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-neutral-500">Data Streams</p>
                            <p className="text-white">{conn.data_streams.join(', ')}</p>
                          </div>
                          <div>
                            <p className="text-neutral-500">Latency</p>
                            <p className="text-white">{conn.latency_ms}ms</p>
                          </div>
                          <div>
                            <p className="text-neutral-500">Throughput</p>
                            <p className="text-white">{conn.throughput_mbps} Mbps</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Security Features */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Security Features Enabled</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {vranStatus.security_features_enabled.map((feature, i) => (
                        <Badge key={feature} variant="outline" className="text-green-400 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" /> {feature}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Processing Stats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-neutral-500 text-sm">Flows/sec</p>
                        <p className="text-xl font-bold text-white">{(vranStatus.processing_stats.flows_per_second / 1000).toFixed(0)}K</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 text-sm">Packets/sec</p>
                        <p className="text-xl font-bold text-white">{(vranStatus.processing_stats.packets_per_second / 1000000).toFixed(0)}M</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 text-sm">Threats Blocked/24h</p>
                        <p className="text-xl font-bold text-orange-400">{vranStatus.processing_stats.threats_blocked_24h.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 text-sm">Added Latency</p>
                        <p className="text-xl font-bold text-green-400">{vranStatus.processing_stats.latency_added_ms}ms</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="capacity">
          {capacityData && (
            <Card className="bg-[#242424] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Capacity Planning & Forecasting</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-[#1a1a1a] rounded-lg p-4">
                      <p className="text-neutral-500 text-sm">Total Capacity</p>
                      <p className="text-2xl font-bold text-white">{capacityData.current_state.total_capacity_tbps} Tbps</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-4">
                      <p className="text-neutral-500 text-sm">Currently Utilized</p>
                      <p className="text-2xl font-bold text-white">{capacityData.current_state.utilized_tbps} Tbps</p>
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-4">
                      <p className="text-neutral-500 text-sm">Monthly Growth</p>
                      <p className="text-2xl font-bold text-orange-400">{capacityData.growth_metrics.monthly_growth_rate}%</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-semibold mb-4">Recommendations</h4>
                    <div className="space-y-3">
                      {capacityData.recommendations.map((rec, i) => (
                        <div key={rec} className="bg-[#1a1a1a] rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={
                                rec.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                                rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-green-500/20 text-green-400'
                              }>
                                {rec.priority}
                              </Badge>
                              <span className="text-white">{rec.action}</span>
                            </div>
                            <p className="text-sm text-neutral-500">{rec.timeline} • ROI: {rec.roi_months} months</p>
                          </div>
                          <span className="text-orange-400 font-bold">{rec.estimated_cost}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="traffic">
          {trafficData && (
            <Card className="bg-[#242424] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Traffic Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-white font-semibold mb-4">Protocol Breakdown</h4>
                    <div className="space-y-2">
                      {Object.entries(trafficData.protocol_breakdown).map(([proto, data]) => (
                        <div key={proto} className="flex items-center justify-between">
                          <span className="text-sm text-neutral-300">{proto}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-black/30 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-orange-500 rounded-full"
                                style={{ width: `${data.percent}%` }}
                              />
                            </div>
                            <span className="text-sm text-white w-12 text-right">{data.percent}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-4">Peak Hours</h4>
                    <div className="space-y-2">
                      {trafficData.peak_hours.map((peak, i) => (
                        <div key={`peak-${i}`} className="flex items-center justify-between bg-[#1a1a1a] rounded p-2">
                          <span className="text-sm text-neutral-300">{peak.hour}</span>
                          <Badge className={peak.utilization > 90 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {peak.utilization}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="assets">
          <Card className="bg-[#242424] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Network Assets</CardTitle>
              <Button className="bg-orange-500 hover:bg-orange-600">
                Register Asset
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-neutral-400">Asset management coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NetFlowAnalytics;
